import React, { useMemo, useContext, useState, useCallback } from 'react';
import slugify from 'slugg';

import { AppContext } from 'src/components/app-context';

import { H3Text } from '@prisjakt/ui/text/variants';

import { Product, AdvizedExpert, ToplistHeadline } from '../../../../../types';
import * as S from './styles';

import { useExpertContentClickTracker } from 'src/hooks/expert-content-click-tracking';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';

type CategoryPageExpertToplistProps = {
  products?: Product[] | null;
  isTopList?: boolean;
};

export const CategoryPageExpertToplist = (
  props: CategoryPageExpertToplistProps
) => {
  const { t } = useContext(AppContext);
  const showButtonTextOptions = useMemo(
    () => ({
      closed: t('symbols', 'symbol.expand'),
      open: t('symbols', 'symbol.collapse'),
    }),
    [t]
  );

  const [showRest, setShowRest] = useState(false);
  const [showButtonText, setButtonText] = useState(
    showButtonTextOptions.closed
  );
  const { products: expertProducts, isTopList = true } = props;

  const toggleProducts = useCallback(() => {
    setButtonText(
      showRest ? showButtonTextOptions.closed : showButtonTextOptions.open
    );
    setShowRest(!showRest);
  }, [showRest, showButtonTextOptions]);

  if (!Array.isArray(expertProducts) || !expertProducts.length) {
    return null;
  }

  const MAX_PRODUCTS = 4;
  const expertsFirst = expertProducts.slice(0, MAX_PRODUCTS);
  const rest = expertProducts.slice(MAX_PRODUCTS);

  return (
    <S.ProductToplistWrapper>
      {expertsFirst!.map((expertProduct, index) => {
        return (
          <a
            href={expertProduct.pathName}
            key={expertProduct.name}
            onClick={() => {
              trackInternalNavigationClick({
                scope: InternalNavigationScope.EXPERT_TOP_LIST,
                nextPage: expertProduct.pathName,
                index,
              });
            }}
          >
            {isTopList && <S.ListPosition>{index + 1}</S.ListPosition>}
            <S.ProductRow>
              {expertProduct.media.first && (
                <img src={expertProduct.media.first} alt={expertProduct.name} />
              )}
              <S.ProductName>{expertProduct.name}</S.ProductName>
            </S.ProductRow>
          </a>
        );
      })}
      {showRest && (
        <div>
          {rest!.map((expertProduct, index) => {
            return (
              <a href={expertProduct.pathName} key={expertProduct.name}>
                {isTopList && (
                  <S.ListPosition>{MAX_PRODUCTS + index + 1}</S.ListPosition>
                )}
                <S.ProductRow>
                  {expertProduct.media.first && (
                    <img
                      src={expertProduct.media.first}
                      alt={expertProduct.name}
                    />
                  )}
                  <S.ProductName>{expertProduct.name}</S.ProductName>
                </S.ProductRow>
              </a>
            );
          })}
        </div>
      )}
      {rest.length > 0 && (
        <S.ShowMoreButton onClick={toggleProducts}>
          {showRest ? <S.ProductsArrow /> : <S.ProductsArrow down="true" />}
          {showRest ? showButtonText : `${showButtonText} (${rest.length})`}
        </S.ShowMoreButton>
      )}
    </S.ProductToplistWrapper>
  );
};

type Props = {
  products?: Product[];
  expert: AdvizedExpert;
  headline: ToplistHeadline;
};

export default function ProductToplist({ products, expert, headline }: Props) {
  const { linkTo, t, renderMode } = useContext(AppContext);
  const { trackToplistClick } = useExpertContentClickTracker();

  const compareUrl = linkTo('productCompare', {
    products,
  });

  if (!Array.isArray(products) || !products.length) {
    return null;
  }

  return (
    <S.ProductToplist data-test="ExpertProductToplist">
      <H3Text>{t('expert', 'toplist.products')}</H3Text>
      <S.Link
        to={compareUrl}
        isExternal={renderMode === 'native'}
        onClick={() => {
          trackToplistClick(headline.id, expert.id);
          trackInternalNavigationClick({
            scope: InternalNavigationScope.TOPLIST_COMPARE_PRODUCTS,
            nextPage: compareUrl,
          });
        }}
      >
        {t('expert', 'toplist.products-compare')}
      </S.Link>

      {products.map((product, index) => (
        <a
          className="product-tab-link"
          href={`#${slugify(`${expert.name}-${product.name}`)}`}
          key={product.name}
        >
          <S.ProductRow data-test="ExpertProductToplistRow">
            {product.media.first && (
              <img
                width={S.IMAGE_SIZE}
                src={product.media.first}
                alt={product.name}
              />
            )}
            <S.ProductName>
              {index + 1}. {product.name}
            </S.ProductName>
          </S.ProductRow>
        </a>
      ))}
    </S.ProductToplist>
  );
}
